import { useEffect, useState, useCallback } from "react";
import { Package, Layers } from "lucide-react";
import { toast } from "sonner";
import {
  adminListProducts, adminCreateProduct, adminUpdateProduct, adminDeleteProduct,
  adminProductsBulkUpdate, adminProductsBulkDelete, adminDuplicateProduct,
  adminProductsReorder,
} from "../../lib/api";
import AdminListingManager from "./AdminListingManager";
import { AField, ImageField } from "./shared";
import SanmarSyncTab from "./SanmarSyncTab";
import SSSyncTab from "./SSSyncTab";
import { X, Save } from "lucide-react";

export function ProductsTab({ pw }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    adminListProducts(pw).then((data) => { setItems(data); setLoading(false); });
  }, [pw]);
  useEffect(reload, [reload]);

  return (
    <>
      <AdminListingManager
        items={items}
        loading={loading}
        mode="custom"
        storageKey="tc_admin_custom"
        onReload={reload}
        onEdit={setEditing}
        onNew={() => setCreating(true)}
        updateItem={(id, patch) => adminUpdateProduct(id, patch, pw)}
        deleteItem={(id) => adminDeleteProduct(id, pw)}
        duplicateItem={(id) => adminDuplicateProduct(id, pw)}
        bulkUpdate={(payload) => adminProductsBulkUpdate(payload, pw)}
        bulkDelete={(payload) => adminProductsBulkDelete(payload, pw)}
        reorderItems={(ids) => adminProductsReorder(ids, pw)}
        thumbFor={(p) => p.image}
        priceOf={(p) => Number(p.base_price ?? 0)}
        viewUrl={(p) => `/customize/${p.slug}`}
      />
      {(editing || creating) && (
        <ProductForm
          item={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSave={async (data) => {
            try {
              if (editing) await adminUpdateProduct(editing.id, data, pw);
              else await adminCreateProduct(data, pw);
              reload();
              setEditing(null); setCreating(false);
              toast.success("Saved");
            } catch { toast.error("Save failed"); }
          }}
        />
      )}
    </>
  );
}

export function ProductForm({ item, onClose, onSave }) {
  const [form, setForm] = useState({
    name: item?.name || "",
    slug: item?.slug || "",
    category: item?.category || "tshirt",
    base_price: item?.base_price || 29.99,
    description: item?.description || "",
    image: item?.image || "",
    mockup_type: item?.mockup_type || "tshirt",
    active: item?.active ?? true,
    colors: (item?.colors || ["#000000", "#FFFFFF"]).join(","),
    sizes: (item?.sizes || ["S", "M", "L", "XL"]).join(","),
    cost_per_unit: item?.cost_per_unit ?? 0,
  });
  const on = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="surface-card max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="product-form">
        <div className="p-6 border-b border-[#222] flex justify-between items-center">
          <h3 className="font-display text-3xl uppercase">{item ? "Edit" : "New"} Product</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-6 grid sm:grid-cols-2 gap-4">
          <AField label="Name" value={form.name} onChange={on("name")} />
          <AField label="Slug" value={form.slug} onChange={on("slug")} />
          <AField label="Category" value={form.category} onChange={on("category")} />
          <AField label="Mockup Type" value={form.mockup_type} onChange={on("mockup_type")} />
          <AField label="Base Price (CAD)" type="number" value={form.base_price} onChange={on("base_price")} />
          <AField label="Cost / Unit (internal, CAD)" type="number" value={form.cost_per_unit} onChange={on("cost_per_unit")} />
          <ImageField label="Image" value={form.image} onChange={(v) => setForm((f) => ({ ...f, image: v }))} />
          <AField label="Colors (comma-separated hex)" value={form.colors} onChange={on("colors")} fullWidth />
          <AField label="Sizes (comma-separated)" value={form.sizes} onChange={on("sizes")} fullWidth />
          <div className="sm:col-span-2">
            <label className="font-mono text-[10px] tracking-widest text-zinc-500">DESCRIPTION</label>
            <textarea value={form.description} onChange={on("description")} rows={3} className="w-full bg-black border border-[#222] px-3 py-2 mt-1 font-body text-sm outline-none resize-none focus:border-[var(--tc-red)]" />
          </div>
          <label className="flex items-center gap-2 font-mono text-xs">
            <input type="checkbox" checked={form.active} onChange={on("active")} /> ACTIVE
          </label>
        </div>
        <div className="p-6 border-t border-[#222] flex justify-end gap-3">
          <button onClick={onClose} className="btn-tc-secondary">Cancel</button>
          <button
            data-testid="save-product-btn"
            onClick={() => onSave({
              ...form,
              base_price: parseFloat(form.base_price) || 0,
              cost_per_unit: parseFloat(form.cost_per_unit) || 0,
              colors: form.colors.split(",").map((s) => s.trim()).filter(Boolean),
              sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
            })}
            className="btn-tc-primary flex items-center gap-2"
          >
            <Save size={16} /> Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ===== READY-MADE CRUD =====

// iter59: nest SanMar Sync as a sub-tab of Custom Products so the top-level
// admin nav stays compact. Room to add S&S (and other supplier syncs) here later.
const SUB_TABS = [
  { k: "manage", l: "Manage", icon: Package },
  { k: "sanmar", l: "SanMar Sync", icon: Layers },
  { k: "ss", l: "S&S Sync", icon: Layers },
];

function ProductsTabWithSubtabs({ pw }) {
  const [sub, setSub] = useState(() => localStorage.getItem("tc_products_subtab") || "manage");
  const setSubTab = (k) => { setSub(k); localStorage.setItem("tc_products_subtab", k); };
  return (
    <div className="space-y-5" data-testid="products-tab-with-subtabs">
      <div className="flex flex-wrap gap-2 border-b border-[#1a1a1a] pb-2" data-testid="products-subtabs">
        {SUB_TABS.map(({ k, l, icon: Icon }) => (
          <button
            key={k}
            type="button"
            onClick={() => setSubTab(k)}
            data-testid={`products-subtab-${k}`}
            className={`px-4 py-2 font-mono text-[11px] tracking-widest border flex items-center gap-2 transition-colors ${
              sub === k
                ? "border-[var(--tc-red)] bg-[var(--tc-red)]/10 text-white"
                : "border-[#222] text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"
            }`}
          >
            <Icon size={12} /> {l}
          </button>
        ))}
      </div>
      {sub === "manage" && <ProductsTab pw={pw} />}
      {sub === "sanmar" && <SanmarSyncTab pw={pw} />}
      {sub === "ss" && <SSSyncTab pw={pw} />}
    </div>
  );
}

export default ProductsTabWithSubtabs;
